import { type CSSProperties } from 'react';

import { extract } from './extract-util.js';

/**
 * Defines the layout size props.
 * @public
 */
export interface LayoutSizeProps {
  /** CSS width property */
  width?: CSSProperties['width'];
  /** CSS min width property */
  minWidth?: CSSProperties['minWidth'];
  /** CSS max width property */
  maxWidth?: CSSProperties['maxWidth'];
  /** CSS height property */
  height?: CSSProperties['height'];
  /** CSS min height property */
  minHeight?: CSSProperties['minHeight'];
  /** CSS max height property */
  maxHeight?: CSSProperties['maxHeight'];
}

/**
 * Keys that will define the layout size.
 * @public
 */
const layoutSizePropKeys: Array<keyof LayoutSizeProps> = [
  'width',
  'minWidth',
  'maxWidth',
  'height',
  'minHeight',
  'maxHeight',
];

/**
 * Will extract layout sizing relevant styles from the passed props and
 * will return a defaulted styles object as well as the remaining props excluding
 * the sizing relevant props.
 * @internal
 */
export function getLayoutSizeStyles<T extends LayoutSizeProps>(
  props: T,
  defaults: Partial<LayoutSizeProps> = {},
): [
  sizeStyles: Partial<LayoutSizeProps>,
  remainingProps: Omit<T, keyof LayoutSizeProps>,
] {
  const { extracted, rest } = extract(props, layoutSizePropKeys);
  const { extracted: extractedDefaults } = extract(
    defaults,
    layoutSizePropKeys,
  );

  return [{ ...extractedDefaults, ...extracted }, rest];
}
